import React, { useEffect, useMemo, useState } from 'react';
import { getMediaUrl } from '../api';

const BannersPage = ({
  bannerForm,
  setBannerForm,
  addBanner,
  updateBanner,
  deleteBanner,
  bannerMessage,
  banners,
}) => {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [editingId, setEditingId] = useState('');

  useEffect(() => {
    setPage(1);
  }, [query, pageSize]);

  const editingBanner = useMemo(
    () => (banners || []).find((b) => String(b._id) === String(editingId)),
    [banners, editingId]
  );

  useEffect(() => {
    if (editingId && !editingBanner) {
      setEditingId('');
      setBannerForm({ title: '', redirect_to: '', image: null });
    }
  }, [editingId, editingBanner, setBannerForm]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (banners || []).filter((b) => {
      if (!q) return true;
      return String(b.title || '').toLowerCase().includes(q);
    });
  }, [banners, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const startEdit = (banner) => {
    setEditingId(String(banner?._id || ''));
    setBannerForm({
      title: banner?.title || '',
      redirect_to: banner?.redirect_to || '',
      image: null,
    });
  };

  const cancelEdit = () => {
    setEditingId('');
    setBannerForm({ title: '', redirect_to: '', image: null });
  };

  const handleSubmit = async () => {
    if (editingId) {
      await updateBanner(editingId);
      setEditingId('');
      return;
    }
    await addBanner();
  };

  const handleRemove = async (banner) => {
    const ok = window.confirm(`Remove banner "${banner?.title || 'Untitled'}"?`);
    if (!ok) return;
    if (String(banner?._id) === String(editingId)) {
      cancelEdit();
    }
    await deleteBanner(banner?._id);
  };

  return (
    <div className="card">
      <h3>{editingId ? 'Edit Banner' : 'Banners'}</h3>
      <div className="grid">
        <div>
          <label>Title</label>
          <input
            value={bannerForm.title}
            onChange={(e) => setBannerForm({ ...bannerForm, title: e.target.value })}
          />
        </div>
        <div>
          <label>Redirect</label>
          <input
            value={bannerForm.redirect_to}
            onChange={(e) => setBannerForm({ ...bannerForm, redirect_to: e.target.value })}
          />
        </div>
        <div>
          <label>{editingId ? 'Image (optional)' : 'Image'}</label>
          <input
            type="file"
            onChange={(e) => setBannerForm({ ...bannerForm, image: e.target.files[0] })}
          />
        </div>
      </div>
      {editingId && editingBanner?.image ? (
        <div className="product-thumb" style={{ margin: '8px 0' }}>
          <img src={getMediaUrl(editingBanner.image)} alt={editingBanner.title || 'Banner'} />
        </div>
      ) : null}
      <div className="row" style={{ marginTop: 8 }}>
        <button onClick={handleSubmit}>{editingId ? 'Update Banner' : 'Add Banner'}</button>
        {editingId ? (
          <button className="secondary" onClick={cancelEdit}>
            Cancel Edit
          </button>
        ) : null}
      </div>
      {bannerMessage ? <div className="muted">{bannerMessage}</div> : null}
      <div className="filters">
        <div className="filter-row">
          <div>
            <label>Title</label>
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search title" />
          </div>
          <div>
            <label>Page Size</label>
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
        <div className="filter-actions">
          <div className="muted">
            Showing {paged.length} of {filtered.length}
          </div>
          <div className="pager">
            <button className="secondary" disabled={currentPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Prev
            </button>
            <span className="pager-info">
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="secondary"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        </div>
      </div>
      <div className="list">
        {paged.map((b) => (
          <div key={b._id} className="list-item">
            {b.image ? (
              <div className="product-thumb" style={{ marginBottom: 8 }}>
                <img src={getMediaUrl(b.image)} alt={b.title || 'Banner'} />
              </div>
            ) : null}
            <strong>{b.title || 'Banner'}</strong>
            <div className="muted">{b.redirect_to || ''}</div>
            <div className="row" style={{ marginTop: 8 }}>
              <button className="secondary" onClick={() => startEdit(b)}>
                Edit
              </button>
              <button className="secondary order-cancel-trigger" onClick={() => handleRemove(b)}>
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BannersPage;
